import toast from 'react-hot-toast'

export interface ErrorResponse {
  status?: number
  message?: string
  errors?: { [key: string]: string }
  timestamp?: string
  path?: string
}

/**
 * Trata erros HTTP do backend e exibe mensagens amigáveis
 * Marca o erro como tratado para evitar toasts duplicados nos hooks
 */
export const handleApiError = (error: any, showToast: boolean = true): void => {
  // Verificar se a resposta é uma string simples (backend pode retornar string direto)
  if (error.response?.data && typeof error.response.data === 'string') {
    if (showToast) {
      const message = error.response.data
      // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
      const is2FAError = message.toLowerCase().includes('código de verificação') || 
                        message.toLowerCase().includes('código 2fa') ||
                        message.toLowerCase().includes('verificação') ||
                        message.toLowerCase().includes('2fa') ||
                        message.toLowerCase().includes('solicite outro')
      
      const errorDuration = is2FAError ? 10000 : 8000
      toast.error(message, {
        duration: errorDuration
      })
    }
    error._errorHandled = true
    return
  }

  const errorResponse: ErrorResponse = error.response?.data

  if (errorResponse) {
    const message = errorResponse.message || 'Erro desconhecido'
    const status = errorResponse.status

    // Mostrar mensagem amigável baseada no status
    if (showToast) {
      // Verificar se é erro relacionado a 2FA para dar mais tempo de leitura
      const is2FAError = message.toLowerCase().includes('código de verificação') || 
                        message.toLowerCase().includes('código 2fa') ||
                        message.toLowerCase().includes('verificação') ||
                        message.toLowerCase().includes('2fa') ||
                        message.toLowerCase().includes('solicite outro')
      
      const errorDuration = is2FAError ? 10000 : 8000 // 10 segundos para erros 2FA, 8 para outros
      
      switch (status) {
        case 401:
          // Usar mensagem específica do backend se disponível
          if (message && message !== 'Erro desconhecido') {
            // Verificar se é mensagem de usuário inativo
            if (message.toLowerCase().includes('inativo')) {
              toast.error('Usuário inativo. Entre em contato com o administrador.', {
                duration: errorDuration
              })
            } else {
              toast.error(message, {
                duration: errorDuration
              })
            }
          } else {
            toast.error('Email ou senha inválidos', {
              duration: errorDuration
            })
          }
          break

        case 403:
          const forbiddenMessage = message || 'Você não tem permissão para realizar esta ação. Apenas administradores podem executar esta operação.'
          toast.error(forbiddenMessage, {
            duration: 10000 // 10 segundos para dar tempo de ler
          })
          break

        case 404:
          toast.error(message || 'Recurso não encontrado.', {
            duration: errorDuration
          })
          break

        case 409:
          // Tratar conflitos (email duplicado, etc)
          if (message.includes('email') || message.includes('Email')) {
            toast.error('Este email já está cadastrado.', {
              duration: errorDuration
            })
          } else {
            toast.error(message, {
              duration: errorDuration
            })
          }
          break

        case 400:
          // Tratar erros de validação
          if (errorResponse.errors) {
            // Mostrar erros específicos por campo
            Object.keys(errorResponse.errors).forEach((campo) => {
              toast.error(`${formatFieldName(campo)}: ${errorResponse.errors![campo]}`, {
                duration: errorDuration
              })
            })
          } else {
            // Usar mensagem específica do backend se disponível
            toast.error(message || 'Erro de validação nos dados enviados', {
              duration: errorDuration
            })
          }
          break

        case 500:
          // Erro interno - usar mensagem do backend se disponível
          toast.error(message || 'Erro ao realizar login. Tente novamente.', {
            duration: errorDuration
          })
          break

        default:
          toast.error(message || 'Ocorreu um erro. Tente novamente.', {
            duration: errorDuration
          })
      }
    }

    // Marcar erro como tratado para evitar toasts duplicados
    error._errorHandled = true
  } else {
    // Erro de conexão ou sem resposta
    if (showToast) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Tempo de espera esgotado. Tente novamente.', {
          duration: 8000
        })
      } else if (!error.response) {
        toast.error('Erro de conexão. Verifique sua internet.', {
          duration: 8000
        })
      } else {
        toast.error('Ocorreu um erro. Tente novamente.', {
          duration: 8000
        })
      }
    }
    error._errorHandled = true
  }
}

/**
 * Verifica se o erro já foi tratado (mostrou toast)
 */
export const isErrorHandled = (error: any): boolean => {
  return error?._errorHandled === true
}

/**
 * Formata o nome do campo para exibição (ex: "email" -> "Email")
 */
const formatFieldName = (field: string): string => {
  const fieldNames: { [key: string]: string } = {
    email: 'Email',
    senha: 'Senha',
    cpf: 'CPF',
    telefone: 'Telefone',
    nome: 'Nome',
    password: 'Senha',
  }

  return fieldNames[field.toLowerCase()] || field.charAt(0).toUpperCase() + field.slice(1)
}

/**
 * Extrai mensagem de erro amigável do erro HTTP
 * Trata tanto string simples quanto objeto ErrorResponse
 */
export const getErrorMessage = (error: any): string => {
  // Verificar se a resposta é uma string simples (backend pode retornar string direto)
  if (error.response?.data && typeof error.response.data === 'string') {
    return error.response.data
  }

  const errorResponse: ErrorResponse = error.response?.data

  if (errorResponse) {
    const status = errorResponse.status

    switch (status) {
      case 401:
        // Usar mensagem específica do backend se disponível
        if (errorResponse.message) {
          // Verificar se é mensagem de usuário inativo
          if (errorResponse.message.toLowerCase().includes('inativo')) {
            return 'Usuário inativo. Entre em contato com o administrador.'
          }
          return errorResponse.message
        }
        return 'Email ou senha inválidos'
      case 403:
        return errorResponse.message || 'Você não tem permissão para realizar esta ação. Apenas administradores podem executar esta operação.'
      case 404:
        return errorResponse.message || 'Recurso não encontrado.'
      case 409:
        if (errorResponse.message?.includes('email') || errorResponse.message?.includes('Email')) {
          return 'Este email já está cadastrado.'
        }
        return errorResponse.message || 'Conflito na operação.'
      case 400:
        // Tratar erros de validação com detalhes
        if (errorResponse.errors) {
          // Retornar primeiro erro de validação ou mensagem geral
          const firstError = Object.keys(errorResponse.errors)[0]
          if (firstError) {
            return errorResponse.errors[firstError]
          }
        }
        // Usar mensagem específica do backend se disponível
        return errorResponse.message || 'Erro de validação nos dados enviados'
      case 500:
        // Erro interno - usar mensagem do backend se disponível
        return errorResponse.message || 'Erro ao realizar login. Tente novamente.'
      default:
        // Usar mensagem específica do backend se disponível
        return errorResponse.message || 'Ocorreu um erro. Tente novamente.'
    }
  }

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Tempo de espera esgotado. Tente novamente.'
  }

  if (!error.response) {
    return 'Erro de conexão. Verifique sua internet.'
  }

  return 'Ocorreu um erro. Tente novamente.'
}

/**
 * Verifica se o erro é de validação (400 com errors)
 */
export const isValidationError = (error: any): boolean => {
  return error.response?.status === 400 && error.response?.data?.errors
}

/**
 * Retorna os erros de validação por campo
 */
export const getValidationErrors = (error: any): { [key: string]: string } | null => {
  if (isValidationError(error)) {
    return error.response.data.errors
  }
  return null
}

